import { ReviewWithUser } from '@/contracts/userReviews';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Star } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ReviewCardProps {
  review: ReviewWithUser;
  showActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

/**
 * Review Card Component
 * Displays user review with rating, content, and metadata
 */
export function ReviewCard({ review, showActions = false, onEdit, onDelete }: ReviewCardProps) {
  const initials = review.user_full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase() || review.user_email?.[0]?.toUpperCase() || '?';

  const timeAgo = formatDistanceToNow(new Date(review.created_at), { addSuffix: true });

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6 space-y-4">
        {/* Header: User info + Rating */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar>
              <AvatarImage src={review.user_avatar_url || undefined} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">
                {review.user_full_name || 'Anonymous'}
              </p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{timeAgo}</span>
                {review.verified_purchase && (
                  <Badge variant="secondary" className="text-xs">
                    Verified Purchase
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Rating Stars */}
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`h-4 w-4 ${
                  star <= review.rating
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-muted-foreground'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Review Title */}
        <h3 className="font-semibold text-lg">{review.title}</h3>

        {/* Review Content */}
        <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
          {review.content}
        </p>

        {/* Actions (Edit/Delete) */}
        {showActions && (
          <div className="flex gap-2 pt-2 border-t">
            {onEdit && (
              <button
                onClick={onEdit}
                className="text-sm text-primary hover:underline"
              >
                Edit
              </button>
            )}
            {onDelete && (
              <button
                onClick={onDelete}
                className="text-sm text-destructive hover:underline"
              >
                Delete
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
